import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Upload, Row, notification } from 'antd';
import { FileDoneOutlined, UploadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getDocument, uploadVerifiedDocument } from '@app/api/pre-clearance.api';
import { PreClearanceVerifiedDocumentRequest } from '@app/models/pre_clearance/PreClearanceRequest';
import { generateExportCertificate, uploadCertifiedDocument } from '@app/api/export.api';

interface Certificate {
  key: string;
  name: string;
  generatedCertificateUrl?: string;
  completedCertificateUrl?: string;
  type: 'LETTER' | 'CERTIFICATE' | 'EXTENSION';
  certificateName?: string;
  letterName?: string;
  extensionName?: string;
}

interface CertificateListProps {
  id: string | null;
  exportCertificates: any[];
  onGenerate?: () => void;
}

const CommonCertificateList: React.FC<CertificateListProps> = ({ id, exportCertificates, onGenerate }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userType = user?.usertype || '';
  const [uploading, setUploading] = useState(false);

  const certificateData: Certificate[] = (
    Array.isArray(exportCertificates) ? exportCertificates : exportCertificates ? [exportCertificates] : []
  ).map((cert, index) => ({
    key: `cert-${cert.export_id}-${index}`,
    name: cert.code,
    type: 'CERTIFICATE' as const,
    generatedCertificateUrl: cert.generated_certificate_url || '',
    completedCertificateUrl: cert.certified_certificate_url || '',
    certificateName: cert.certified_certificate_url || undefined,
  }));

  const handleGenerate = async () => {
    if (!id) return;
    try {
      setUploading(true);
      await generateExportCertificate(Number(id));
      notification.success({ message: 'Success', description: 'Certificate generation started.' });
      onGenerate?.();
    } catch (error) {
      console.error('Generate error:', error);
      notification.error({ message: 'Error', description: 'Failed to generate certificate.' });
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async (file: File, certificateKey: string, id: number) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await uploadCertifiedDocument(Number(id), formData);
      if (response) {
        notification.success({ message: 'Upload Success', description: 'Certified document uploaded successfully.' });
        onGenerate?.(); // re-fetch parent data to reflect new URL
      } else {
        notification.error({ message: 'Upload Error', description: 'Failed to upload certified document.' });
      }
    } catch (error) {
      console.error('Upload error:', error);
      notification.error({ message: 'Upload Error', description: 'Failed to upload certified document.' });
    } finally {
      setUploading(false);
    }
  };

  const columns = [
    {
      title: 'Code',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Generated Document',
      key: 'generatedCertificate',
      render: (record: Certificate) =>
        record.generatedCertificateUrl ? (
          <Button size="small" type="primary" onClick={() => window.open(record.generatedCertificateUrl, '_blank')}>
            View
          </Button>
        ) : (
          'Not Available yet'
        ),
    },
    {
      title: 'Certified Document',
      key: 'completedCertificate',
      render: (record: Certificate) => (
        <Row align="middle" gutter={[8, 8]} justify="space-between">
          {record.certificateName ? (
            <>
              <a href={record.certificateName} target="_blank" rel="noopener noreferrer">
                View Certified Document
              </a>
              {(userType === 'ADMIN' || userType === 'SUPERADMIN') && (
                <Upload
                  beforeUpload={(file) => {
                    handleUpload(file, record.key, Number(record.type));
                    return false;
                  }}
                  showUploadList={false}
                >
                  <Button icon={<UploadOutlined />} size="small" loading={uploading} style={{ marginLeft: '6px' }}>
                    Re-upload
                  </Button>
                </Upload>
              )}
            </>
          ) : (
            (userType === 'ADMIN' || userType === 'SUPERADMIN') && (
              <Upload
                beforeUpload={(file) => {
                  handleUpload(file, record.key, Number(id)!);
                  return false;
                }}
                showUploadList={false}
              >
                <Button icon={<UploadOutlined />} size="small" loading={uploading}>
                  Upload Certified Document
                </Button>
              </Upload>
            )
          )}
        </Row>
      ),
    },
  ];

  return (
    <Table
      dataSource={certificateData}
      columns={columns}
      rowKey="key"
      locale={{
        emptyText: (
          <div style={{ padding: '32px 0', textAlign: 'center' }}>
            <p style={{ color: '#8c8c8c' }}>No certificates available</p>
            {(userType === 'ADMIN' || userType === 'SUPERADMIN') && (
              <Button type="primary" icon={<FileDoneOutlined />} loading={uploading} onClick={handleGenerate}>
                Generate Certificate
              </Button>
            )}
          </div>
        ),
      }}
    />
  );
};

export default CommonCertificateList;
